import React, { useEffect, useState } from "react";
import { api } from "../../services/api";

interface PartnershipRequest {
  id: number;
  full_name: string;
  email: string;
  company_name: string;
  shipping_address1?: string;
  shipping_address2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_postal_code?: string;
  shipping_country?: string;
  social_facebook?: string;
  social_instagram?: string;
  social_tiktok?: string;
  collaboration_type: string;
  message: string;
  status: "pending" | "approved" | "rejected" | "contacted";
  created_at: string;
  coupon_code?: string;
  coupon_value?: number;
  created_user_id?: number;
}

interface ApprovalForm {
  couponCode: string;
  couponValue: number;
  couponType: "fixed" | "percentage";
  commissionPercentage: number;
}

interface EmailRecord {
  id: number;
  request_id: number;
  user_id?: number | null;
  recipient_email: string;
  subject: string;
  body: string;
  status: string;
  created_at: string;
}

export default function PartnershipsManager() {
  const [requests, setRequests] = useState<PartnershipRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("pending");
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [selectedRequest, setSelectedRequest] =
    useState<PartnershipRequest | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState<ApprovalForm>({
    couponCode: "",
    couponValue: 0,
    couponType: "fixed",
    commissionPercentage: 0,
  });
  const [approving, setApproving] = useState(false);
  const [approvalSuccess, setApprovalSuccess] = useState<{
    username: string;
    tempPassword: string;
    couponCode: string;
  } | null>(null);

  // Email modal / history
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", content: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState<EmailRecord[]>([]);

  // User access modal (view username / regenerate temporary password)
  const [showUserAccessModal, setShowUserAccessModal] = useState(false);
  const [userAccessLoading, setUserAccessLoading] = useState(false);
  const [userAccessInfo, setUserAccessInfo] = useState<{
    id: number;
    username: string;
    email: string;
    password: string;
    role?: string;
    is_active?: number;
  } | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(
    null,
  );

  // Load requests
  useEffect(() => {
    loadRequests(filter, pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadRequests = async (status: string, page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const endpoint =
        status === "all"
          ? `/partnerships.php?action=list&page=${page}`
          : `/partnerships.php?action=list&status=${status}&page=${page}`;

      const response: any = await api.get(endpoint);

      if (response.success) {
        setRequests(response.requests);
        setPagination(response.pagination);
      } else {
        throw new Error(response.message || "Failed to load requests");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalModalOpen = (request: PartnershipRequest) => {
    setSelectedRequest(request);
    setApprovalForm({
      couponCode: `${request.company_name?.replace(/\s+/g, "_").toUpperCase() || "PARTNER"}_2025`,
      couponValue: 0,
      couponType: "fixed",
      commissionPercentage: 0,
    });
    setApprovalSuccess(null);
    setShowApprovalModal(true);
  };

  const handleApprove = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !selectedRequest ||
      !approvalForm.couponCode ||
      approvalForm.couponValue <= 0
    ) {
      setError("Coupon code and value are required");
      return;
    }

    if (
      approvalForm.commissionPercentage < 0 ||
      approvalForm.commissionPercentage > 100
    ) {
      setError("Commission percentage must be between 0 and 100");
      return;
    }

    try {
      setApproving(true);
      setError(null);

      const response: any = await api.put(`/partnerships.php?action=approve`, {
        request_id: selectedRequest.id,
        coupon_code: approvalForm.couponCode,
        coupon_value: approvalForm.couponValue,
        coupon_type: approvalForm.couponType,
        commission_percentage: approvalForm.commissionPercentage,
      });

      if (response.success) {
        setApprovalSuccess({
          username: response.username,
          tempPassword: response.temporary_password,
          couponCode: response.coupon_code,
        });
      } else {
        throw new Error(response.message || "Approval failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (requestId: number) => {
    if (!window.confirm("Are you sure you want to reject this request?"))
      return;

    try {
      const response: any = await api.put(`/partnerships.php?action=reject`, {
        request_id: requestId,
      });

      if (response.success) {
        loadRequests(filter, pagination.page);
      } else {
        throw new Error(response.message || "Rejection failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDisablePartner = async (request: PartnershipRequest) => {
    if (
      !window.confirm(
        `Are you sure you want to disable partner access for ${request.full_name}? They will no longer be able to login.`,
      )
    )
      return;

    try {
      const response: any = await api.put(
        `/partnerships.php?action=disable_partner`,
        {
          user_id: request.created_user_id,
          request_id: request.id,
        },
      );

      if (response.success) {
        loadRequests(filter, pagination.page);
        alert("Partner access has been disabled successfully.");
      } else {
        throw new Error(response.message || "Failed to disable partner");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const loadEmailHistory = async (requestId: number) => {
    try {
      const response: any = await api.get(
        `/partnerships.php?action=email_history&request_id=${requestId}`,
      );

      if (response.success) {
        setEmailHistory(response.emails || []);
      } else {
        setEmailHistory([]);
      }
    } catch (err) {
      setEmailHistory([]);
    }
  };

  const handleOpenUserAccessModal = async (request: PartnershipRequest) => {
    setSelectedRequest(request);
    setTemporaryPassword(null);
    setUserAccessInfo(null);
    setShowUserAccessModal(true);
    setUserAccessLoading(true);

    try {
      const resp: any = await api.get(
        `/partnerships.php?action=user_access&request_id=${request.id}`,
      );

      if (resp.success && resp.user) {
        setUserAccessInfo(resp.user);
      } else {
        setUserAccessInfo(null);
        setError(resp.message || "Failed to load user access info");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setUserAccessInfo(null);
    } finally {
      setUserAccessLoading(false);
    }
  };

  const handleResetTempPassword = async () => {
    if (!selectedRequest) return;

    if (!window.confirm("Generate a new temporary password for this user?"))
      return;

    try {
      setResettingPassword(true);
      setTemporaryPassword(null);
      setError(null);

      const resp: any = await api.post(`/partnerships.php?action=user_access`, {
        request_id: selectedRequest.id,
      });

      if (resp.success && resp.temporary_password) {
        setTemporaryPassword(resp.temporary_password);
      } else {
        throw new Error(resp.message || "Failed to reset password");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleEmailModalOpen = (request: PartnershipRequest) => {
    setSelectedRequest(request);

    // Basic template which includes partner-specific details already
    // Updated template: mention sending product samples and include tracking placeholder
    const template =
      `Hello ${request.full_name || ""},\n\n` +
      `Thank you for your interest in partnering with us${request.company_name ? ` at ${request.company_name}` : ""}.` +
      `\n\nWe will be sending you product samples to review. The package will include:\n- Products included: [LIST_PRODUCTS_HERE]\n\nOnce the shipment is prepared we will provide the courier and tracking code below so you can follow the delivery.` +
      `\n\nTracking code: [ENTER_TRACKING_CODE_HERE]\n\n` +
      (request.coupon_code
        ? `Your coupon code is: ${request.coupon_code}\n\n`
        : "") +
      `If you have any questions, reply to this email and we'll get back to you.` +
      `\n\nBest regards,\nThe Team`;

    setEmailForm({
      subject: `${request.company_name ? request.company_name + " - " : ""}Partnership Follow-up`,
      content: template,
    });

    setShowEmailModal(true);
    loadEmailHistory(request.id);
  };

  const handleSendEmail = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedRequest) return;

    if (!emailForm.subject || !emailForm.content) {
      setError("Subject and content are required");
      return;
    }

    try {
      setSendingEmail(true);
      setError(null);

      const response: any = await api.post(
        `/partnerships.php?action=send_email`,
        {
          request_id: selectedRequest.id,
          recipient_email: selectedRequest.email,
          subject: emailForm.subject,
          body: emailForm.content,
        },
      );

      if (response.success) {
        // refresh history
        await loadEmailHistory(selectedRequest.id);
        alert(response.message || "Email sent successfully");
      } else {
        throw new Error(response.message || "Failed to send email");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: "warning",
      approved: "success",
      rejected: "danger",
      contacted: "info",
    };
    return `bg-${statusMap[status] || "secondary"}`;
  };

  const getCollaborationTypeBadge = (type: string) => {
    const typeMap: Record<string, string> = {
      influencer: "Influencer/Creator",
      "brand-partnership": "Brand Partnership",
      affiliate: "Affiliate Program",
      "bulk-order": "Bulk Order",
      retailer: "Retailer/Wholesale",
      other: "Other",
    };
    return typeMap[type] || type;
  };

  const buildRequestDetails = (request: PartnershipRequest) => {
    const addressLines = [
      request.shipping_address1,
      request.shipping_address2,
      [
        request.shipping_city,
        request.shipping_state,
        request.shipping_postal_code,
      ]
        .filter(Boolean)
        .join(", ") || undefined,
      request.shipping_country,
    ].filter(Boolean);

    const socials = [
      request.social_facebook
        ? `Facebook: ${request.social_facebook}`
        : "Facebook: -",
      request.social_instagram
        ? `Instagram: ${request.social_instagram}`
        : "Instagram: -",
      request.social_tiktok ? `TikTok: ${request.social_tiktok}` : "TikTok: -",
    ];

    return [
      `Message:\n${request.message || "-"}`,
      "",
      "Shipping Address:",
      addressLines.length ? addressLines.join("\n") : "-",
      "",
      "Social Links:",
      socials.join("\n"),
    ].join("\n");
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="h3">Partnership Requests</h1>
          <p className="text-muted">
            Manage collaboration and partnership inquiries
          </p>
        </div>
      </div>

      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-4">
        <ul className="nav nav-tabs">
          {(["pending", "approved", "rejected", "all"] as const).map((f) => (
            <li key={f} className="nav-item">
              <button
                className={`nav-link d-flex align-center ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f !== "all" && (
                  <span className="badge hidden white text-white bg-secondary ms-2">
                    {requests.filter((r) => r.status === f).length}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Requests Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : requests.length === 0 ? (
        <div className="alert alert-info">No partnership requests found.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td className="fw-medium">{request.full_name}</td>
                    <td>{request.email}</td>
                    <td>{request.company_name || "-"}</td>
                    <td>
                      <small className="badge bg-light text-dark">
                        {getCollaborationTypeBadge(request.collaboration_type)}
                      </small>
                    </td>
                    <td>
                      <span
                        className={`badge ${getStatusBadge(request.status)}`}
                      >
                        {request.status}
                      </span>
                    </td>
                    <td>
                      <small>
                        {new Date(request.created_at).toLocaleDateString()}
                      </small>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          className="btn btn-sm btn-info me-1"
                          onClick={() => alert(buildRequestDetails(request))}
                          title="View message"
                        >
                          <i className="fa fa-eye"></i>
                        </button>

                        <button
                          className="btn btn-sm btn-primary me-1"
                          onClick={() => handleEmailModalOpen(request)}
                          title="Send email"
                        >
                          <i className="fa fa-envelope"></i>
                        </button>

                        {request.status === "pending" && (
                          <>
                            <button
                              className="btn btn-sm btn-success me-1"
                              onClick={() => handleApprovalModalOpen(request)}
                            >
                              <i className="bi bi-check-circle"></i> Approve
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleReject(request.id)}
                            >
                              <i className="bi bi-x-circle"></i> Reject
                            </button>
                          </>
                        )}

                        {request.status === "approved" && (
                          <>
                            {request.coupon_code && (
                              <span className="badge bg-success me-1">
                                {request.coupon_code}
                              </span>
                            )}
                            {request.created_user_id && (
                              <>
                                <button
                                  className="btn btn-sm btn-secondary me-1"
                                  onClick={() =>
                                    handleOpenUserAccessModal(request)
                                  }
                                  title="User access"
                                >
                                  <i className="bi bi-key"></i> Access
                                </button>

                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleDisablePartner(request)}
                                  title="Disable partner access"
                                >
                                  <i className="bi bi-ban"></i> Disable
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <nav aria-label="Page navigation" className="mt-4">
              <ul className="pagination">
                <li
                  className={`page-item ${pagination.page === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => loadRequests(filter, pagination.page - 1)}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: pagination.pages }).map((_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${pagination.page === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => loadRequests(filter, i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${pagination.page === pagination.pages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => loadRequests(filter, pagination.page + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Partnership Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowApprovalModal(false)}
                ></button>
              </div>

              {approvalSuccess ? (
                <div className="modal-body text-center">
                  <div className="mb-3">
                    <i
                      className="bi bi-check-circle text-success"
                      style={{ fontSize: "3rem" }}
                    ></i>
                  </div>
                  <h5>Partnership Approved!</h5>
                  <p className="text-muted mb-3">
                    Account created successfully. Share these details with the
                    partner:
                  </p>

                  <div className="alert alert-light border mb-3">
                    <div className="mb-2">
                      <small className="text-muted">Username:</small>
                      <div className="font-monospace bg-white p-2 rounded">
                        {approvalSuccess.username}
                      </div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Temporary Password:</small>
                      <div className="font-monospace bg-white p-2 rounded">
                        {approvalSuccess.tempPassword}
                      </div>
                    </div>
                    <div>
                      <small className="text-muted">Coupon Code:</small>
                      <div className="font-monospace bg-white p-2 rounded">
                        {approvalSuccess.couponCode}
                      </div>
                    </div>
                  </div>

                  <p className="text-muted small">
                    They should change their password on first login.
                  </p>

                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => {
                      setShowApprovalModal(false);
                      loadRequests(filter, pagination.page);
                    }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApprove}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">
                        <strong>{selectedRequest.full_name}</strong>
                      </label>
                      <p className="text-muted small mb-0">
                        {selectedRequest.email}
                      </p>
                      <p className="text-muted small">
                        {selectedRequest.company_name}
                      </p>
                    </div>

                    <hr />

                    <div className="mb-3">
                      <label className="form-label">Coupon Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={approvalForm.couponCode}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            couponCode: e.target.value.toUpperCase(),
                          })
                        }
                        placeholder="e.g., PARTNER_2025"
                        required
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Coupon Type *</label>
                        <select
                          className="form-select"
                          value={approvalForm.couponType}
                          onChange={(e) =>
                            setApprovalForm({
                              ...approvalForm,
                              couponType: e.target.value as
                                | "fixed"
                                | "percentage",
                            })
                          }
                        >
                          <option value="fixed">Fixed Amount ($)</option>
                          <option value="percentage">Percentage (%)</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Value *</label>
                        <input
                          type="number"
                          className="form-control"
                          step="0.01"
                          min="0"
                          value={approvalForm.couponValue}
                          onChange={(e) =>
                            setApprovalForm({
                              ...approvalForm,
                              couponValue: parseFloat(e.target.value),
                            })
                          }
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Partner Commission (%) *
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.01"
                        min="0"
                        max="100"
                        value={approvalForm.commissionPercentage}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            commissionPercentage:
                              parseFloat(e.target.value) || 0,
                          })
                        }
                        placeholder="e.g., 10 for 10%"
                        required
                      />
                      <small className="text-muted">
                        Commission is calculated on the discounted order total
                      </small>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowApprovalModal(false)}
                      disabled={approving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={approving}
                    >
                      {approving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Approving...
                        </>
                      ) : (
                        "Approve & Create Account"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Access Modal */}
      {showUserAccessModal && selectedRequest && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  User Access - {selectedRequest.full_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowUserAccessModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                {userAccessLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border" role="status"></div>
                  </div>
                ) : userAccessInfo ? (
                  <>
                    <div className="mb-2">
                      <strong>Username:</strong> {userAccessInfo.username}
                    </div>
                    <div className="mb-2">
                      <strong>Email:</strong> {userAccessInfo.email}
                    </div>
                    <div className="mb-2">
                      <strong>Password:</strong> {userAccessInfo.password}
                    </div>
                    <div className="mb-2">
                      <strong>Active:</strong>{" "}
                      {userAccessInfo.is_active ? "Yes" : "No"}
                    </div>

                    <div className="mt-3">
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={handleResetTempPassword}
                        disabled={resettingPassword}
                      >
                        {resettingPassword ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Generating...
                          </>
                        ) : (
                          "Regenerate Temporary Password"
                        )}
                      </button>
                      {temporaryPassword && (
                        <div className="mt-2">
                          <div className="alert alert-success mb-0">
                            Temporary password:{" "}
                            <strong>{temporaryPassword}</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="alert alert-info">
                    No user information found.
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUserAccessModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedRequest && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Send Email to {selectedRequest.full_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEmailModal(false)}
                ></button>
              </div>

              <form onSubmit={handleSendEmail}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">To</label>
                    <div className="form-control-plaintext">
                      {selectedRequest.email}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Subject *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={emailForm.subject}
                      onChange={(e) =>
                        setEmailForm({ ...emailForm, subject: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Content *</label>
                    <textarea
                      className="form-control"
                      rows={8}
                      value={emailForm.content}
                      onChange={(e) =>
                        setEmailForm({ ...emailForm, content: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowEmailModal(false)}
                      disabled={sendingEmail}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Sending...
                        </>
                      ) : (
                        "Send Email"
                      )}
                    </button>
                  </div>

                  <hr />
                  <h6 className="mb-2">Email History</h6>
                  {emailHistory.length === 0 ? (
                    <div className="alert alert-info">No emails sent yet.</div>
                  ) : (
                    <div className="list-group small">
                      {emailHistory.map((em) => (
                        <div key={em.id} className="list-group-item">
                          <div className="d-flex w-100 justify-content-between">
                            <strong>{em.subject}</strong>
                            <small className="text-muted">
                              {new Date(em.created_at).toLocaleString()}
                            </small>
                          </div>
                          <div className="text-muted">
                            To: {em.recipient_email}
                          </div>
                          <div
                            className="mt-2"
                            dangerouslySetInnerHTML={{ __html: em.body }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
